import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Anexo, Endpoint, IeAnexo, Report, ReportService, shouldRenderMobile, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { HpmsCustomContentCollapseEvent } from '@philips-emr/hpms-stipulator-components';
import { HPMSOnOpen, HPMSOnRenderAction } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { ActionAPI, Button, FieldsetAPI, FormAPI, PanelAPI, RepeaterAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { ButtonTheme, Color, Placement } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount, OnSelect } from '@philips-emr/tws-core/lib/client';
import { Data } from '@philips-emr/tws-core/lib/component/context/data-context';
import { FetchEvent, RenderEvent, SelectEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import './requisicao-form-controller.css';

import { RequisicaoFormAliases as Aliases, FragmentNames } from '../../codegen';
import { TipoOrigemAnexo } from '../anexo-tiss/anexo-tiss-controller';
import BaseController from '../BaseController';

enum IeStatus {
  A = 'a',
  C = 'c',
  I = 'i',
  N = 'n',
  P = 'p',
  S = 's',
  U = 'u',
  G = 'g',
  T = 't',
}

enum AnexoTiss {
  OPME = 'OP',
  QUIMIOTERAPIA = 'QU',
}

interface GuiaItem {
  nrSeqItem: number;
  ieTipoItem: string;
  dsStatus: string;
  ieStatus: IeStatus;
  ieTipoAnexo: AnexoTiss;
}

interface RequestData {
  ieRecemNascido: string;
  ieTipoGuia: string;
  ieEstagio: number;
  nrSeqRequisicao: number;
}

interface Diagnostico {
  dsDoenca: string;
  cdDoenca: string;
}

interface Values {
  [key: string]: Diagnostico | undefined;
}

function getStampStatus(ieStatus: IeStatus, label: string): ReactElement | null {
  let stampProps = null;

  switch (ieStatus) {
    case IeStatus.A:
      stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label };
      break;
    case IeStatus.C:
      stampProps = { color: Color.SAFETY_ORANGE, icon: StampIcon.CANCELED_OUTLINE, label };
      break;
    case IeStatus.I:
      stampProps = { className: 'c-requisicao_form-stamp__stamp--color', icon: StampIcon.CLOCK, label };
      break;
    case IeStatus.N:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;
    case IeStatus.P:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case IeStatus.S:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case IeStatus.U:
      stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label };
      break;
    case IeStatus.G:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;
    case IeStatus.T:
      stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label };
      break;
    default:
      return null;
  }

  return <Stamp {...stampProps} />;
}

const GRUPO_RELATORIO_REQUISICAO = '12';

@Controller({ fragment:  FragmentNames.REQUISICAO_FORM })
class RequisicaoFormController extends BaseController {

  @Inject(Aliases.FIELDSET_RECEM_NASCIDO)
  private fieldsetRecemNascido: FieldsetAPI;
  @Inject(Aliases.FIELDSET_GUIA_INTERNACAO)
  private fieldsetGuiaInternacao: FieldsetAPI;
  @Inject(Aliases.PANEL_CID)
  private panelCid: PanelAPI;
  @Inject(Aliases.REPEATER_REQUEST_DIAGNOSIS)
  private diagnosticoReapter: RepeaterAPI;
  @Inject(Aliases.PANEL_GLOSA_OCORRENCIA)
  private glosaOcorrenciaPanel: PanelAPI;
  @Inject(Aliases.PANEL_ANEXO)
  private anexoPanel: PanelAPI;
  @Inject(Aliases.PANEL_PROD_MAT)
  private panelProdMat: PanelAPI;
  @Inject(Aliases.ACTION_PRINT)
  private print: ActionAPI;
  @Inject(Aliases.REQUEST_DATA_FORM)
  private requestDataForm: FormAPI;
  private reportService: ReportService;

  @OnMount(Aliases.LAYOUT_REQUISICAO_FORM)
  public onMount(): void {
    const { nrSeqRequisicao } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqRequisicao });
    this.reportService = new ReportService(GRUPO_RELATORIO_REQUISICAO, Endpoint.GET.RELATORIOS_REQUISICAO_POR_GRUPO);
  }

  @OnAfterFetch(Aliases.FETCH_REQUEST_DATA)
  public OnAfterRequestDataFetch(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const requestDetailData = event.data as RequestData;

    if (requestDetailData) {
      this.fieldsetRecemNascido.setVisible(requestDetailData.ieRecemNascido === 'S');
      this.fieldsetGuiaInternacao.setVisible(requestDetailData.ieTipoGuia === '1');
    }

    void this.reportService.getRelatoriosByCdGrupo({ nrSeqRequisicao: requestDetailData.nrSeqRequisicao, ieTipoGuia: requestDetailData.ieTipoGuia })
      .then((response: Report[]) => {
        if (response.length === 0) {
          this.print.setEnabled(false);
        }
      });
  }

  @OnAfterFetch(Aliases.FETCH_GLOSA_OCORRENCIA)
  public onAfterFetchGlosaOcorrencia(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const values = event.data as [];

    if (isEmptyArrayOrObject(values)) {
      this.glosaOcorrenciaPanel.setVisible(false);
    }

  }

  @OnAfterFetch(Aliases.FETCH_ANEXO)
  public onAfterFetchAnexo(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const values = event.data as [];

    if (isEmptyArrayOrObject(values)) {
      this.anexoPanel.setVisible(false);
    }

  }

  @OnAfterFetch(Aliases.FETCH_PROD_MAT)
  public onAfterFetchProdMat(event: FetchEvent): void {
    if (!event.data) {
      return;
    }

    const values = event.data as [];

    if (isEmptyArrayOrObject(values)) {
      this.panelProdMat.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_DIAGNOSTICO)
  public onAfterFetchDiagnostico(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    this.setDataReapeater(event.data as Values, this.diagnosticoReapter, this.panelCid, 'diagnosticoRequisicaoAutorizacao');
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_STAMP)
  public onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as GuiaItem;
    const stampExclusao = getStampStatus(ieStatus, dsStatus);

    if (stampExclusao) {
      render((): ReactElement => stampExclusao);
    }
  }

  @HPMSOnOpen(Aliases.HPMS_CUSTOM_CARD_CONTENT_COLLAPSE)
  public onOpenRenderContent(event: HpmsCustomContentCollapseEvent<GuiaItem>): void {
    if (!event || !event.data) {
      return;
    }

    const { data, onOpenRender } = event;
    const parameters = (({ nrSeqItem, ieTipoItem }) => ({ nrSeqItem, ieTipoItem }))(data);
    onOpenRender({ parameters });
  }

  @OnSelect(Aliases.ATTACHMENTS_LIST_ANEXO)
  public onSelectAttachment(event: SelectEvent<Anexo>): void {
    const { value } = event;
    const { nrSeqAnexo, filename } = value;
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const ieTabela = IeAnexo.REQUISICAO_ANEXO;

    FileService.download(Endpoint.GET.GUIA_DOWNLOAD, filename, { ieTabela, nrSeqAnexo, nrSeqContrato }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  @OnClick(Aliases.ACTION_PRINT)
  onClickPrint(): void {
    const record = this.requestDataForm.getRecord();
    this.reportService && this.reportService.abreListaRelatorios({ NR_SEQ_REQUISICAO: record.nrSeqRequisicao, NR_SEQUENCIA_AUTOR: record.nrSeqRequisicao });
  }

  public setDataReapeater(values: Values, repeater: RepeaterAPI, panel: PanelAPI, keyCheck: string): void {
    if (isEmptyArrayOrObject(values)) {
      panel.setVisible(false);
      return;
    }

    const displayTexts: Data[] = [];

    values && Object.keys(values).forEach(key => {
      const displayObj: Data = this.resolveDisplayText(values && values[key]);

      if (displayObj[keyCheck]) {
        displayTexts.push(displayObj);
      }
    }
    );

    repeater.setData(displayTexts as Record<string, Value>[]);
  }

  private resolveDisplayText(diagnostico: Diagnostico | undefined): Data {
    if (!diagnostico) {
      return { diagnosticoRequisicaoAutorizacao: '' };
    }

    const { cdDoenca, dsDoenca } = diagnostico;
    return { diagnosticoRequisicaoAutorizacao: ` ${cdDoenca} - ${dsDoenca} ` };
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_ACTION)
  public onDynamicContentRenderAction(event: RenderEvent): void {
    const { render, data } = event;
    const { nrSeqItem, ieTipoAnexo } = data as unknown as GuiaItem;

    if (shouldRenderMobile() || ![AnexoTiss.OPME, AnexoTiss.QUIMIOTERAPIA].includes(ieTipoAnexo)) {
      return;
    }

    render(() => (
      <Button
        key="report"
        icon="/static/assets/view-details.svg"
        theme={ButtonTheme.QUIET_EMPHASIS}
        value=""
        iconPlacement={Placement.RIGHT}
        onClick={() => this.openModalMaterial(nrSeqItem, ieTipoAnexo)}
        className="c-requisicao-item_item-button-action"
      />
    ));
  }

  private openModalMaterial(nrSeqMat: number, ieTipoAnexo: string): void {
    const { nrSeqRequisicao } = this.historyService.getCurrentRouteParams();
    const nrSeqContrato = this.context.get('nrSeqContrato');

    ModalService.openDialog({
      title: 'historico-autorizacao.detalhes-item',
      fragment: 'anexo-item-prod-mat',
      contextData: {
        nrSeqContrato,
        nrSequencia: nrSeqRequisicao,
        nrSeqMat,
        ieTipoAnexo,
        origem: TipoOrigemAnexo.ANEXOS_TISS_REQUISICOES,
      },
    });
  }

  @HPMSOnRenderAction(Aliases.HPMS_CUSTOM_CARD_PROCEDIMENTO_MATERIAL)
  public onDynamicContentRenderVisualizacao(event: RenderEvent): void {
    const { render, data } = event;
    const { nrSeqItem, ieTipoAnexo } = data as unknown as GuiaItem;

    if (!shouldRenderMobile() || ![AnexoTiss.OPME, AnexoTiss.QUIMIOTERAPIA].includes(ieTipoAnexo)) {
      return;
    }

    render(() => (
      <Button
        key="report"
        icon="/static/assets/view-details.svg"
        theme={ButtonTheme.QUIET_EMPHASIS}
        value=""
        iconPlacement={Placement.RIGHT}
        onClick={() => this.openModalMaterial(nrSeqItem, ieTipoAnexo)}
        className="c-requisicao-item_item-button-action"
      />
    ));
  }
}

export default {
  RequisicaoFormController,
};
